import { useEffect, useMemo, useState } from 'react';
import { valueUpdatesSentence } from '@/lib/valueUpdates';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import {
  commitImport,
  deleteImportTemplate,
  listGraphs,
  previewImport,
  profileSpreadsheet,
  proposeImportSpec,
  reconcileImport,
  saveImportTemplate,
  type ApiFailure,
  type ApiResult,
  type ColumnProfile,
  type ColumnSpec,
  type CommitResponse,
  type GraphInfo,
  type ImportSpec,
  type LoadMode,
  type EntityDecision,
  type EntityDecisions,
  type Normalize,
  type PreviewStatement,
  type ReconcileItem,
  type SheetRow,
  type SpreadsheetPreviewResponse,
  type SpreadsheetProfileResponse,
  type TemplateMatch,
} from '@/lib/api';
import { ConfirmGraphReplaceModal } from '@/components/ConfirmGraphReplaceModal';
import { SaveMappingModal } from '@/components/import/SaveMappingModal';
import { ValidationStep } from '@/components/import/ValidationStep';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import { useMaxLoadBytes } from '@/lib/usePages';
import { useOntologySchema } from '@/lib/useOntology';
import { ALL_GRAPHS, graphDisplayName, newGraphIri } from '@/components/GraphSelector';
import { ColumnsPage } from '@/components/import/ColumnsPage';
import { PropertiesPage } from '@/components/import/PropertiesPage';
import { RulesPage } from '@/components/import/RulesPage';
import { SheetPanel } from '@/components/import/SheetPanel';
import { createClass } from '@/lib/useOntology';
import {
  columnKind,
  currentDraft,
  draftNeeds,
  needsInWords,
  draftStarted,
  EMPTY_DRAFT,
  isThingColumn,
  mappingProblems,
  mappingsToColumns,
  mappingsToNewClasses,
  mintedPredicate,
  specRelationships,
  specToMappings,
  suggestedMappings,
  type ColumnMapping,
  type RelationshipDraft,
  sameMapping,
  withContextSuggestion,
} from '@/lib/columnMapping';
import type { MapContext } from '@/components/FolderAnalysisReport';
import { cn } from '@/lib/utils';
import { Message, messageClass } from '@/components/ui/Message';


function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

type Step = 'load' | 'map' | 'relate' | 'rules' | 'validate' | 'done';

const STEPS: { id: Step; label: string }[] = [
  { id: 'load', label: 'Load' },
  { id: 'map', label: 'Columns' },
  { id: 'relate', label: 'Properties' },
  { id: 'rules', label: 'Rules' },
  { id: 'validate', label: 'Validate' },
  { id: 'done', label: 'Done' },
];



/** The default role + normalization when a column is (re)assigned a designation. */
function defaultColumn(name: string, tag: 'instance' | 'datatype_property', inferred: ColumnProfile['inferred']): ColumnSpec {
  const isDate = inferred === 'date';
  const normalize: Normalize[] = isDate ? [{ op: 'date_iso' }] : [];
  switch (tag) {
    case 'instance':
      return {
        column: name,
        role: {
          role: 'instance',
          class: null,
          is_label: true,
          fill_down: false,
          resolution: { mode: 'mint' },
          id_strategy: { kind: 'from_column' },
        },
        normalize: [],
      };
    case 'datatype_property':
      return {
        column: name,
        role: {
          role: 'datatype_property',
          predicate: mintedPredicate(name),
          datatype: isDate ? 'xsd:date' : null,
          required: false,
        },
        normalize,
      };
  }
}

/** Header-row picker: the first raw rows of the sheet, letting the user say
 *  which row holds the column names and where the data starts — for messy
 *  vendor sheets whose header is not row 1. A reusable piece: the T3 assisted
 *  importer surfaces the same control with an AI-proposed header pre-selected. */
function HeaderRowPicker({
  rawRows,
  headerRow,
  dataStartRow,
  disabled,
  onChange,
}: {
  rawRows: string[][];
  headerRow: number;
  dataStartRow: number;
  disabled: boolean;
  onChange: (headerRow: number, dataStartRow: number) => void;
}) {
  const [open, setOpen] = useState(headerRow !== 0);
  if (rawRows.length === 0) return null;
  const cols = Math.max(1, ...rawRows.map((r) => r.length));
  return (
    <div className="rounded-md border border-border text-xs">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className="flex w-full items-center justify-between px-3 py-2 text-left disabled:opacity-50"
      >
        <span>
          <span className="font-medium">Header row:</span> row {headerRow + 1} · data starts at row{' '}
          {dataStartRow + 1}
        </span>
        <span className="text-muted-foreground">{open ? 'Hide' : 'Adjust'}</span>
      </button>
      {open && (
        <div className="border-t border-border p-2">
          <p className="mb-2 text-muted-foreground">
            Click the row that holds the column names. If a header row is repeated or a units row
            follows it, set where the data starts.
          </p>
          <div className="max-h-56 overflow-auto">
            <table className="text-left">
              <tbody>
                {rawRows.map((row, i) => {
                  const isHeader = i === headerRow;
                  const isData = i >= dataStartRow;
                  const isSkipped = !isHeader && !isData;
                  return (
                    <tr
                      key={i}
                      className={cn(
                        'border-t border-border',
                        isHeader && 'bg-primary/10 font-medium',
                        isSkipped && 'text-muted-foreground line-through',
                      )}
                    >
                      <td className="whitespace-nowrap px-2 py-1">
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => onChange(i, i + 1)}
                          className="rounded border border-border px-1.5 py-0.5 hover:bg-muted disabled:opacity-50"
                          title="Use this row as the header"
                        >
                          row {i + 1}
                          {isHeader ? ' ✓' : ''}
                        </button>
                      </td>
                      {Array.from({ length: cols }).map((_, c) => (
                        <td key={c} className="whitespace-nowrap px-2 py-1 font-mono">
                          {row[c] ?? ''}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <label className="mt-2 flex items-center gap-2">
            <span className="text-muted-foreground">Data starts at row</span>
            <input
              type="number"
              min={headerRow + 2}
              value={dataStartRow + 1}
              disabled={disabled}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v >= headerRow + 2) onChange(headerRow, v - 1);
              }}
              className="w-16 rounded-md border border-input bg-background px-2 py-1"
            />
          </label>
        </div>
      )}
    </div>
  );
}

/** A graph as the import's success line names it. */
function placedName(graph: string): string {
  if (graph === 'urn:tres:ontology') return 'the ontology';
  if (graph === 'urn:tres:vocabulary') return 'the vocabulary';
  return graph;
}

export function SpreadsheetImportPanel({
  initialFile,
  initialSheet,
  initialContext,
  uploadsLocked = false,
}: {
  /** Hand a staged file straight into the wizard (the folder plan's "Map"
   * action) — analysis starts immediately. */
  initialFile?: File | null;
  initialSheet?: string;
  /** From the folder report's "Map with a context tag": the rows' thing and
   *  the column that tells repeated rows apart, applied to the suggestions. */
  initialContext?: MapContext;
  /** A load holds the gate — starting or committing an import is banned. */
  uploadsLocked?: boolean;
} = {}) {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('load');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [profile, setProfile] = useState<SpreadsheetProfileResponse | null>(null);
  // Zero-indexed header row + first data row, for the header-row picker. The
  // server echoes what it used; the picker re-analyzes when the user changes it.
  const [headerRow, setHeaderRow] = useState(0);
  const [dataStartRow, setDataStartRow] = useState<number | null>(null);
  const [spec, setSpec] = useState<ImportSpec | null>(null);
  // The template grid's editable state; every change lowers into `spec`, which
  // stays the single source the preview/reconcile/commit calls read.
  const [mappings, setMappings] = useState<ColumnMapping[] | null>(null);
  // A relationship being written in step 2's add row. Held here so going Back
  // and forward keeps it, and Validate waits until it is added or cleared.
  const [draft, setDraft] = useState<RelationshipDraft>(EMPTY_DRAFT);
  // The columns the person is deciding about, highlighted in the sheet panel.
  const [focusColumns, setFocusColumns] = useState<string[]>([]);

  const [reconcileItems, setReconcileItems] = useState<ReconcileItem[] | null>(null);
  const [reconcileTruncated, setReconcileTruncated] = useState(false);
  // Decision-typed reconcile state: link / create_new / ignore per minted IRI.
  // Reconcile runs fresh on ENTRY to Validation (against the confirmed
  // mapping), so stale-match hazards (audit W4/F12) cannot arise by
  // construction — there is no match state while the mapping is editable.
  const [decisions, setDecisions] = useState<EntityDecisions>({});
  // Facts the person left out, kept whole so each can be put back; their ids
  // travel to preview and commit as `ignored_statements`.
  const [leftOut, setLeftOut] = useState<PreviewStatement[]>([]);

  const [preview, setPreview] = useState<SpreadsheetPreviewResponse | null>(null);
  const [previewOffset, setPreviewOffset] = useState(0);
  const [mode, setMode] = useState<LoadMode>('append');
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [result, setResult] = useState<ApiResult<CommitResponse> | null>(null);
  // The saved mapping the current spec started from, and the name the mapping
  // is saved under at commit (blank = not saved).
  const [appliedTemplate, setAppliedTemplate] = useState<TemplateMatch | null>(null);
  const [templateName, setTemplateName] = useState('');
  // The mapping as last saved, applied or saved from page 2, so the Import
  // press knows whether the pages hold anything unsaved (61.93).
  const [savedSpec, setSavedSpec] = useState<ImportSpec | null>(null);
  const [askSave, setAskSave] = useState(false);
  // The name the commit saves the mapping under, chosen in the dialog.
  const [saveAs, setSaveAs] = useState<string | null>(null);
  const [saveOutcome, setSaveOutcome] = useState<{ ok: boolean; text: string } | null>(null);

  // Target graph — the data's destination is never implicit. Default to the
  // managed data graph; the user can pick another or create a new one.
  const DATA_GRAPH = 'urn:tres:data';
  const ONTOLOGY_GRAPH = 'urn:tres:ontology';
  const [graphs, setGraphs] = useState<GraphInfo[]>([]);
  const [targetGraph, setTargetGraph] = useState<string>(DATA_GRAPH);
  const [newGraph, setNewGraph] = useState('');
  const creatingGraph = targetGraph === '__new__';
  const effectiveGraph = creatingGraph ? newGraphIri(newGraph) : targetGraph;
  const targetName = creatingGraph ? newGraph.trim() : graphDisplayName(targetGraph);

  const maxLoadBytes = useMaxLoadBytes().data ?? null;
  // Declared ontology (classes + properties) for the mapping step's predicate/
  // class type-ahead — pick an existing term instead of inventing a duplicate.
  const ontologySchema = useOntologySchema(ALL_GRAPHS, token);

  /** A class created while mapping is declared in the ontology at once
   *  (ruled 2026-09-30), so every later choice draws from the ontology. */
  async function declareClass(c: { label: string; parent: string; uri: string }) {
    if (!token) return { ok: false as const, error: 'Sign in to create a class.' };
    const r = await createClass(
      { graph: ONTOLOGY_GRAPH, label: c.label, super_classes: c.parent ? [c.parent] : [], uri: c.uri },
      token,
    );
    if (!r.ok) return { ok: false as const, error: r.message };
    await queryClient.invalidateQueries({ queryKey: ['ontology'] });
    return { ok: true as const, uri: r.data.uri };
  }
  const oversize = file !== null && maxLoadBytes !== null && file.size > maxLoadBytes;
  const multiSheet = (profile?.sheet_names.length ?? 0) > 1;

  /** Per-column profile, by name, for samples/type/conformance. */
  const colInfo = useMemo(() => {
    const m = new Map<string, ColumnProfile>();
    for (const c of profile?.profile.columns ?? []) m.set(c.name, c);
    return m;
  }, [profile]);

  // Ruling 4.8: no column is the key. The mapping runs when every column says
  // which ID it describes and at least one column is an ID.
  const hasClassColumn = mappings !== null && mappings.some(isThingColumn);
  // Page 3 exists only when a column is a Rule.
  const hasRules = mappings !== null && mappings.some((m) => columnKind(m) === 'rule' && m.job === 'code');
  const steps = STEPS.filter((s) => s.id !== 'rules' || hasRules);
  const problems = useMemo(() => (mappings ? mappingProblems(mappings) : []), [mappings]);
  const mappingValid = mappings !== null && problems.length === 0;
  // What step 1 settles: every column answered, and every Class column's class.
  const columnsSettled = !problems.some((p) => p.field === 'header' || p.field === 'class');
  // The add row as it stands against the mapping, so Validate is held only by
  // what the row shows.
  const pending = currentDraft(draft, mappings ?? []);

  /** Which sheet column writes which property, so Validate can name the
   *  column to change. */
  const columnOfPredicate = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of spec?.columns ?? []) {
      if (c.role.role === 'object_property' || c.role.role === 'datatype_property') {
        m.set(c.role.predicate, c.column);
      }
    }
    return m;
  }, [spec]);

  // A file handed in from the folder plan starts the wizard immediately.
  useEffect(() => {
    if (initialFile) {
      setFile(initialFile);
      void analyze(initialSheet, initialFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile, initialSheet]);

  function showError(r: ApiFailure) {
    setError(r.message);
  }

  async function analyze(
    sheet?: string,
    fileOverride?: File,
    headerOverride?: number,
    dataStartOverride?: number | null,
  ) {
    const f = fileOverride ?? file;
    if (!token || !f) return;
    const hdr = headerOverride ?? headerRow;
    const dstart = dataStartOverride === undefined ? dataStartRow : dataStartOverride;
    setBusy('analyze');
    setError(null);
    setPreview(null);
    setResult(null);
    setReconcileItems(null);
    setDecisions({});
    setLeftOut([]);
    const r = await profileSpreadsheet(f, token, sheet, hdr, dstart ?? undefined);
    if (!r.ok) {
      setBusy(null);
      showError(r);
      setProfile(null);
      return;
    }
    setProfile(r.data);
    // Keep the picker in sync with what the server actually used.
    setHeaderRow(r.data.header_row);
    setDataStartRow(r.data.data_start_row);
    // Load the named-graph list so Preview can show a real target selector. A
    // failure is SAID, not swallowed — the target select otherwise rendered
    // only "+ New graph…" with no explanation (audit W13).
    void listGraphs(token).then((g) => {
      if (g.ok) setGraphs(g.data.graphs);
      else setError(`Could not load the graph list — ${g.message}; re-open this step to retry.`);
    });
    // A saved mapping whose columns this file has, all of them, is what the
    // user wants far more often than a fresh proposal: apply it and say so. A
    // file with more columns is how a mapping grows — the new ones arrive as
    // Skip, to be mapped and saved again under the same name.
    const fits =
      r.data.templates.find((t) => t.match === 'exact') ?? r.data.templates.find((t) => t.match === 'subset');
    if (fits) {
      applyTemplate(fits);
      if (fits.extra_columns.length > 0) {
        // The columns the mapping has not seen are the person's to answer;
        // suggest for them as for a fresh file.
        const prop = await proposeImportSpec({ namespace: 'resource:', profile: r.data.profile, rows: r.data.rows }, token);
        const hints = new Map(prop.ok ? specToMappings(prop.data).map((m) => [m.column, m]) : []);
        setMappings((ms) =>
          ms &&
          ms.map((m) =>
            fits.extra_columns.includes(m.column) ? { ...m, unanswered: true, suggestion: hints.get(m.column) } : m,
          ),
        );
      }
      setStep('map');
      setBusy(null);
      return;
    }
    setAppliedTemplate(null);
    setTemplateName('');
    setSavedSpec(null);
    const prop = await proposeImportSpec({ namespace: 'resource:', profile: r.data.profile, rows: r.data.rows }, token);
    if (!prop.ok) {
      // The fallback guess is not the server proposal the copy promises —
      // proceed, but say what happened (audit W13).
      setError(
        `The mapping proposal failed (${prop.message}) — starting from a basic guess; review every column.`,
      );
    }
    const proposed: ImportSpec = prop.ok
      ? prop.data
      : {
          // Minimal local default so the user can still proceed: first column the subject.
          namespace: 'resource:',
          columns: r.data.columns.map((name, i) =>
            defaultColumn(name, i === 0 ? 'instance' : 'datatype_property', colInfo.get(name)?.inferred ?? 'text'),
          ),
        };
    // Suggestions only: every column starts unanswered and nothing is read
    // until the person answers it or accepts the suggestion.
    const fresh = suggestedMappings(specToMappings(proposed));
    const suggested =
      initialContext && f === initialFile
        ? withContextSuggestion(fresh, initialContext.subject, initialContext.column)
        : fresh;
    setSpec({ ...proposed, columns: mappingsToColumns(suggested, proposed.columns) });
    setMappings(suggested);
    setStep('map');
    setBusy(null);
  }

  function applyTemplate(t: TemplateMatch) {
    setSpec(t.spec);
    setMappings(specToMappings(t.spec));
    setDecisions({});
    setAppliedTemplate(t);
    setTemplateName(t.name);
    setSavedSpec(t.spec);
  }

  async function proposeFresh() {
    if (!token || !profile) return;
    setBusy('analyze');
    const prop = await proposeImportSpec({ namespace: 'resource:', profile: profile.profile, rows: profile.rows }, token);
    setBusy(null);
    if (!prop.ok) {
      showError(prop);
      return;
    }
    const suggested = suggestedMappings(specToMappings(prop.data));
    setSpec({ ...prop.data, columns: mappingsToColumns(suggested, prop.data.columns) });
    setMappings(suggested);
    setDecisions({});
    setAppliedTemplate(null);
    setTemplateName('');
    setSavedSpec(null);
  }

  async function removeTemplate(t: TemplateMatch) {
    if (!token || !profile) return;
    if (!window.confirm(`Delete the saved mapping “${t.name}” for this site?`)) return;
    const r = await deleteImportTemplate(t.id, token);
    if (!r.ok) {
      showError(r);
      return;
    }
    setProfile({ ...profile, templates: profile.templates.filter((x) => x.id !== t.id) });
    if (appliedTemplate?.id === t.id) {
      setAppliedTemplate(null);
      setTemplateName('');
    }
  }

  /** Search for existing matches against the CONFIRMED mapping and pre-apply
   *  the best exact match per value (suggest-and-correct). Runs on entry to
   *  Validation — after designation, when the class constraints mean
   *  something. Returns the merged decision set for chaining into preview
   *  (React state has not settled inside the same tick). */
  /** `includeMinted` distinguishes the user pressing Recheck from the
   *  automatic pass on the way to Validation. See the api.ts note. */
  async function runReconcile(
    useSpec: ImportSpec,
    rows: SheetRow[],
    includeMinted = false,
  ): Promise<EntityDecisions | null> {
    if (!token) return null;
    setError(null);
    const r = await reconcileImport(
      { spec: useSpec, rows, include_minted: includeMinted },
      token,
    );
    if (!r.ok) {
      showError(r);
      return null;
    }
    setReconcileItems(r.data.items);
    setReconcileTruncated(r.data.truncated);
    // Auto-link only to an exact/label match whose class is confirmed to be
    // the one the column declares — a same-label entity of a different class
    // (a family named like its model) is offered but never auto-applied.
    // Semantic ("similar") matches likewise need the user's confirmation.
    const suggested: EntityDecisions = {};
    for (const item of r.data.items) {
      const top = item.candidates[0];
      if (top && top.match_kind === 'label' && top.class_match) {
        suggested[item.minted_iri] = { action: 'link', uri: top.uri };
      }
    }
    // The user's own choices SURVIVE a re-check (old code replaced the whole
    // map, wiping every manual link/unlink — audit W5). Manual picks win for
    // values still in the item set; picks for values no longer probed drop.
    const valid = new Set(r.data.items.map((i) => i.minted_iri));
    const kept = Object.fromEntries(Object.entries(decisions).filter(([k]) => valid.has(k)));
    const merged = { ...suggested, ...kept };
    setDecisions(merged);
    return merged;
  }

  async function doPreview(
    offset = 0,
    decisionsArg?: EntityDecisions,
    leftOutArg?: PreviewStatement[],
  ): Promise<boolean> {
    if (!token || !profile || !spec || !mappingValid) return false;
    setBusy('preview');
    setError(null);
    const r = await previewImport(
      {
        spec,
        rows: profile.rows,
        decisions: decisionsArg ?? decisions,
        ignored_statements: (leftOutArg ?? leftOut).map((s) => s.id),
        offset,
      },
      token,
    );
    setBusy(null);
    if (!r.ok) {
      showError(r);
      return false;
    }
    setPreview(r.data);
    setPreviewOffset(offset);
    return true;
  }

  /** Map → Validation: fresh matches against the confirmed mapping, then the
   *  statement preview those decisions produce. */
  async function goValidate() {
    if (!profile || !spec) return;
    setBusy('reconcile');
    const merged = await runReconcile(spec, profile.rows);
    if (merged === null) {
      setBusy(null);
      return;
    }
    if (await doPreview(0, merged)) setStep('validate');
  }

  function handleDecide(mintedIri: string, decision: EntityDecision) {
    const next = { ...decisions, [mintedIri]: decision };
    setDecisions(next);
    void doPreview(0, next);
  }

  function handleLeaveOut(statements: PreviewStatement[]) {
    const next = [...leftOut, ...statements.filter((s) => !leftOut.some((x) => x.id === s.id))];
    setLeftOut(next);
    void doPreview(0, undefined, next);
  }

  function handlePutBack(statementIds: string[]) {
    const next = leftOut.filter((s) => !statementIds.includes(s.id));
    setLeftOut(next);
    void doPreview(0, undefined, next);
  }

  async function handleRecheck() {
    if (!profile || !spec) return;
    setBusy('reconcile');
    // The user asked, so look everywhere — including columns designated to
    // mint, whose matches they would otherwise never be offered.
    const merged = await runReconcile(spec, profile.rows, true);
    if (merged === null) {
      setBusy(null);
      return;
    }
    await doPreview(previewOffset, merged);
  }

  /** Save the mapping as it stands, without importing: a mapping made on a
   *  short sheet is saved, applied to the full one, extended and saved again. */
  async function saveMapping() {
    const name = templateName.trim();
    if (!token || !profile || !spec || !name) return;
    // A relationship typed into the add row and not added is not in the
    // mapping; saving now would keep a mapping without it, and say nothing.
    if (draftStarted(pending)) {
      setSaveOutcome({
        ok: false,
        text: 'The mapping was not saved: an ObjectProperty in the add row is not added yet. Press Add to keep it, or clear the row.',
      });
      return;
    }
    setBusy('save');
    setSaveOutcome(null);
    const r = await saveImportTemplate({ name, columns: profile.columns, spec }, token);
    setBusy(null);
    if (r.ok) setSavedSpec(spec);
    const relationships = specRelationships(spec).length;
    setSaveOutcome(
      r.ok
        ? {
            ok: true,
            text: `Saved as ${name}: ${profile.columns.length} columns and ${relationships} relationship${relationships === 1 ? '' : 's'}. A file with these columns, or more, gets it automatically.`,
          }
        : { ok: false, text: `The mapping was not saved: ${r.message}` },
    );
  }

  /** Whether the pages hold a mapping that is not saved: nothing saved yet,
   *  or the saved one differs from what the pages now say. */
  const mappingUnsaved = spec !== null && (savedSpec === null || !sameMapping(spec, savedSpec));

  /** The Import press. A mapping not saved asks first (61.93); a replace
   *  then asks for the graph; then the commit runs. */
  function pressImport() {
    if (mappingUnsaved) {
      setAskSave(true);
      return;
    }
    proceedImport(null);
  }

  function proceedImport(name: string | null) {
    setAskSave(false);
    setSaveAs(name);
    if (mode === 'replace') setConfirmReplace(true);
    else void doCommit(name);
  }

  async function doCommit(name: string | null) {
    if (!token || !profile || !spec || !effectiveGraph) return;
    setConfirmReplace(false);
    setBusy('commit');
    setError(null);
    const r = await commitImport(
      {
        spec,
        rows: profile.rows,
        mode,
        graph: effectiveGraph,
        decisions,
        ignored_statements: leftOut.map((s) => s.id),
        ...(file?.name ? { source_label: file.name } : {}),
        ...(name ? { save_as_template: { name, columns: profile.columns } } : {}),
      },
      token,
    );
    setBusy(null);
    setResult(r);
    if (r.ok) {
      if (name) {
        setTemplateName(name);
        setSavedSpec(spec);
      }
      setStep('done');
      invalidateAfterDataWrite(queryClient);
    } else {
      // The ONE mutation that swallowed failures (audit W1): a bad graph or a
      // substrate error looked like nothing happened.
      showError(r);
    }
  }

  function reset() {
    setStep('load');
    setFile(null);
    setProfile(null);
    setSpec(null);
    setMappings(null);
    setDraft(EMPTY_DRAFT);
    setReconcileItems(null);
    setDecisions({});
    setLeftOut([]);
    setPreview(null);
    setResult(null);
    setError(null);
    setTargetGraph(DATA_GRAPH);
    setNewGraph('');
    setAppliedTemplate(null);
    setTemplateName('');
    setSavedSpec(null);
    setSaveAs(null);
    setAskSave(false);
  }

  // What went wrong, shown at the button that was pressed.
  const stepError = error ? (
    <Message kind="warning" density="compact" role="alert">
      {error}
    </Message>
  ) : null;

  const btnPrimary = cn(
    'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground',
    'hover:bg-primary/90 disabled:opacity-50',
  );
  const btnSecondary = cn(
    'rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground',
    'hover:bg-secondary/80 disabled:opacity-50',
  );
  const btnGhost = cn(
    'rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground',
    'hover:bg-muted disabled:opacity-50',
  );
  const inputCls = 'rounded-md border border-input bg-background px-2 py-1 text-xs';

  return (
    <div className="space-y-6">
      {/* Stepper */}
      <ol className="flex flex-wrap items-center gap-1 text-xs">
        {steps.map((s, i) => {
          const active = s.id === step;
          const done = steps.findIndex((x) => x.id === step) > i;
          return (
            <li key={s.id} className="flex items-center gap-1">
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 font-medium',
                  active && 'bg-primary text-primary-foreground',
                  done && !active && 'bg-secondary text-secondary-foreground',
                  !active && !done && 'bg-muted text-muted-foreground',
                )}
              >
                {i + 1}. {s.label}
              </span>
              {i < steps.length - 1 && <span className="text-muted-foreground">→</span>}
            </li>
          );
        })}
      </ol>

      {/* ---- Step: Load ---- */}
      {step === 'load' && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Turn a spreadsheet into QROS-native statements. Upload a file; the importer proposes a
            mapping you refine column by column. Nothing is written until you commit — you pick
            the target graph on the preview step.
          </p>
          <label className="block">
            <span className="text-sm font-medium">Spreadsheet file (.csv or .xlsx)</span>
            <input
              type="file"
              accept=".csv,.tsv,.xlsx,.xls,.ods,.json,.txt,.md,.html,.htm"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setError(null);
              }}
              disabled={busy !== null || uploadsLocked}
              className={cn(
                'mt-1 block w-full text-sm',
                'file:mr-4 file:rounded-md file:border-0 file:bg-secondary',
                'file:px-4 file:py-2 file:text-sm file:font-medium file:text-secondary-foreground',
                'hover:file:bg-secondary/80 disabled:opacity-50',
              )}
            />
            {file && (
              <span className="mt-1 block text-xs text-muted-foreground">
                {file.name} — {formatBytes(file.size)}
              </span>
            )}
            {oversize && (
              <p className="mt-2 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                This file is {formatBytes(file!.size)}, over the {formatBytes(maxLoadBytes!)}{' '}
                per-file upload limit.
              </p>
            )}
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void analyze()}
              disabled={busy !== null || !file || oversize}
              className={btnPrimary}
            >
              {busy === 'analyze' ? 'Analyzing…' : 'Analyze'}
            </button>
            {stepError}
            {multiSheet && profile && (
              <label className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Sheet</span>
                <select
                  value={profile.selected_sheet ?? ''}
                  onChange={(e) => void analyze(e.target.value)}
                  disabled={busy !== null}
                  className={inputCls}
                >
                  {profile.sheet_names.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
      )}

      {/* ---- Step: Map / Refine ---- */}
      {step === 'map' && profile && spec && (
        <div className="space-y-4">
          {/* Workbook sheet picker — HERE, not only on the Load step. Analyze
              auto-advances to this step, so a picker living on Load was
              unreachable on the first pass and a 5-tab workbook silently
              imported whichever sheet the server defaulted to (audit D9). */}
          {multiSheet && (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">
                Sheet ({profile.sheet_names.length} in this workbook)
              </span>
              <select
                value={profile.selected_sheet ?? ''}
                onChange={(e) => void analyze(e.target.value)}
                disabled={busy !== null}
                className={inputCls}
              >
                {profile.sheet_names.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          )}
          {/* Header-row picker — for a messy sheet whose header is not row 1
              (daypart labels / a repeated header above the data). The columns
              below reflect the chosen header, so a wrong pick is obvious here. */}
          <HeaderRowPicker
            rawRows={profile.raw_rows}
            headerRow={headerRow}
            dataStartRow={dataStartRow ?? headerRow + 1}
            disabled={busy !== null}
            onChange={(h, d) => void analyze(profile.selected_sheet ?? undefined, undefined, h, d)}
          />
          {/* The sheet stays in view above every decision (4.10): the panel on
              each page, here with the row count beside it. */}
          <p className="text-xs text-muted-foreground">
            Your sheet: {profile.rows.length.toLocaleString()} rows. The first two stay in view above the
            columns; the column under the pointer is highlighted.
          </p>
          {profile.notes.length > 0 && (
            <ul className="rounded-md border border-border p-3 text-xs">
              <li className="mb-1 font-medium">How this file was read as a table</li>
              {profile.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}

          {(profile.templates.length > 0 || appliedTemplate) && (
            <div className="rounded-md border border-border p-3 text-sm">
              {appliedTemplate ? (
                <p>
                  Using the saved mapping <strong>{appliedTemplate.name}</strong>
                  {(() => {
                    const n = specRelationships(appliedTemplate.spec).length;
                    return n === 0
                      ? ', which holds no ObjectProperty'
                      : `, which holds ${n} ObjectPropert${n === 1 ? 'y' : 'ies'}`;
                  })()}
                  {appliedTemplate.match === 'subset' && appliedTemplate.extra_columns.length > 0 && (
                    <>
                      {' '}
                      — columns it has not mapped yet are not answered:{' '}
                      <span className="font-mono">{appliedTemplate.extra_columns.join(', ')}</span>. Answer them,
                      then save again under the same name to add them to it
                    </>
                  )}
                  {appliedTemplate.match === 'overlap' && (
                    <>
                      {' '}
                      — columns it expected but this file lacks were dropped:{' '}
                      <span className="font-mono">{appliedTemplate.missing_columns.join(', ')}</span>
                    </>
                  )}
                  . Edit below, or{' '}
                  <button
                    type="button"
                    onClick={() => void proposeFresh()}
                    disabled={busy !== null}
                    className="underline"
                  >
                    start again from suggestions
                  </button>
                  .
                </p>
              ) : (
                <p className="font-medium">Saved mappings that fit these columns</p>
              )}
              {profile.templates.some((t) => t.id !== appliedTemplate?.id) && (
                <ul className="mt-2 space-y-1">
                  {profile.templates
                    .filter((t) => t.id !== appliedTemplate?.id)
                    .map((t) => (
                      <li key={t.id} className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => applyTemplate(t)}
                          disabled={busy !== null}
                          className={btnGhost}
                        >
                          Apply
                        </button>
                        <span className="font-medium">{t.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {specRelationships(t.spec).length} ObjectPropert
                          {specRelationships(t.spec).length === 1 ? 'y' : 'ies'} ·{' '}
                          {t.match === 'exact'
                            ? 'same columns'
                            : t.match === 'subset'
                              ? `skips ${t.extra_columns.length} extra column${t.extra_columns.length === 1 ? '' : 's'}`
                              : `missing ${t.missing_columns.join(', ')}`}
                          {t.last_used_at ? ` · last used ${t.last_used_at.slice(0, 10)}` : ''}
                        </span>
                        <button
                          type="button"
                          onClick={() => void removeTemplate(t)}
                          disabled={busy !== null}
                          className="text-xs text-muted-foreground underline"
                          aria-label={`Delete saved mapping ${t.name}`}
                        >
                          delete
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-end justify-end gap-3 border-t border-border pt-4">
            <label className="flex items-center gap-1.5 text-sm">
              <span className="font-medium">Namespace</span>
              <Info
                className="h-3.5 w-3.5 cursor-help text-muted-foreground"
                aria-label="About the namespace"
                tabIndex={0}
                role="img"
              >
                <title>
                  Choose the namespace used for all new records created from the imported sheet.
                  Blank is the default namespace.
                </title>
              </Info>
              <input
                type="text"
                value={spec.namespace}
                onChange={(e) => {
                  // The namespace is part of every minted IRI — link decisions
                  // keyed by those IRIs are stale; validation re-reconciles
                  // fresh on entry, so just drop them (audit W4/F12).
                  setSpec((s) => s && { ...s, namespace: e.target.value });
                  setDecisions({});
                }}
                className={cn(inputCls, 'w-40 font-mono')}
              />
            </label>
          </div>

          <SheetPanel columns={profile.columns} rows={profile.rows} highlight={new Set(focusColumns)} />
          {mappings && (
            <ColumnsPage
              mappings={mappings}
              profiles={colInfo}
              schema={ontologySchema.data ?? null}
              onFocus={setFocusColumns}
              onCreateClass={declareClass}
              onChange={(next) => {
                setMappings(next);
                setSpec(
                  (s) =>
                    s && {
                      ...s,
                      columns: mappingsToColumns(next, s.columns),
                      new_classes: mappingsToNewClasses(next),
                    },
                );
              }}
            />
          )}

          {profile.profile.dependencies.length > 0 && (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-tres-text-primary">Latent entities:</span>{' '}
              {profile.profile.dependencies
                .slice(0, 4)
                .map((d) => `${d.dependent} depends on ${d.determinant}`)
                .join('; ')}
              . These may belong on a separate entity reached by a relationship.
            </p>
          )}

          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setStep('load')} disabled={busy !== null} className={btnGhost}>
              Back
            </button>
            <button
              type="button"
              onClick={() => setStep('relate')}
              disabled={busy !== null || !mappings || !hasClassColumn || !columnsSettled}
              className={btnPrimary}
            >
              Next: the properties →
            </button>
            {mappings && hasClassColumn && !columnsSettled && (
              <span className="text-xs text-muted-foreground">
                Answer every column and choose each Entity column’s class to go on.
              </span>
            )}
            {stepError}
          </div>
        </div>
      )}

      {/* ---- Step: Relationships ---- */}
      {step === 'relate' && mappings && profile && (
        <div className="space-y-4">
          <SheetPanel columns={profile.columns} rows={profile.rows} highlight={new Set(focusColumns)} />
          <PropertiesPage
            mappings={mappings}
            rows={profile.rows}
            dependencies={profile.profile.dependencies}
            draft={draft}
            onDraft={setDraft}
            onFocus={setFocusColumns}
            schema={ontologySchema.data ?? null}
            onChange={(next) => {
              setMappings(next);
              setSpec(
                (s) =>
                  s && {
                    ...s,
                    columns: mappingsToColumns(next, s.columns),
                    new_classes: mappingsToNewClasses(next),
                  },
              );
            }}
          />
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <label className="font-medium" htmlFor="ss-save-name">
                Save this mapping as
              </label>
              <input
                id="ss-save-name"
                type="text"
                value={templateName}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                  setSaveOutcome(null);
                }}
                placeholder="e.g. Monthly sales export"
                maxLength={120}
                className={cn(inputCls, 'w-64')}
              />
              <button
                type="button"
                onClick={() => void saveMapping()}
                disabled={busy !== null || !templateName.trim()}
                className={btnGhost}
              >
                {busy === 'save' ? 'Saving…' : 'Save mapping'}
              </button>
            </div>
            {saveOutcome && (
              <Message kind={saveOutcome.ok ? 'outcome' : 'warning'} density="compact">
                {saveOutcome.text}
              </Message>
            )}
            <p className="text-xs text-muted-foreground">
              Saves without importing. Saving again under the same name replaces that mapping.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setStep('map')} disabled={busy !== null} className={btnGhost}>
              Back
            </button>
            {hasRules ? (
              <button
                type="button"
                onClick={() => setStep('rules')}
                disabled={busy !== null || draftStarted(pending)}
                className={btnPrimary}
              >
                Next: the rules →
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void goValidate()}
                disabled={busy !== null || !mappingValid || draftStarted(pending)}
                className={btnPrimary}
              >
                {busy === 'reconcile'
                  ? 'Matching against the store…'
                  : busy === 'preview'
                    ? 'Building statements…'
                    : 'Validate statements →'}
              </button>
            )}
            {draftStarted(pending) && (
              <Message kind="warning" density="compact">
                {draftNeeds(pending).length === 0
                  ? 'An ObjectProperty in the add row is not added yet. Press Add to keep it, or clear the row.'
                  : `An ObjectProperty in the add row is not added yet: it needs ${needsInWords(draftNeeds(pending))}. Finish it and press Add, or clear the row.`}
              </Message>
            )}
            {!draftStarted(pending) && !mappingValid && !hasRules && (
              <Message kind="warning" density="compact">
                Validate waits on the rows marked above:{' '}
                {[...new Set(problems.filter((p) => p.column).map((p) => p.text))].slice(0, 3).join(' ')}
              </Message>
            )}
            {stepError}
          </div>
        </div>
      )}

      {/* ---- Page 3: Rules ---- */}
      {step === 'rules' && mappings && profile && (
        <div className="space-y-4">
          <SheetPanel columns={profile.columns} rows={profile.rows} highlight={new Set(focusColumns)} />
          <RulesPage
            mappings={mappings}
            rows={profile.rows}
            schema={ontologySchema.data ?? null}
            onFocus={setFocusColumns}
            onChange={(next) => {
              setMappings(next);
              setSpec(
                (s) =>
                  s && {
                    ...s,
                    columns: mappingsToColumns(next, s.columns),
                    new_classes: mappingsToNewClasses(next),
                  },
              );
            }}
          />
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setStep('relate')} disabled={busy !== null} className={btnGhost}>
              Back
            </button>
            <button
              type="button"
              onClick={() => void goValidate()}
              disabled={busy !== null || !mappingValid}
              className={btnPrimary}
            >
              {busy === 'reconcile'
                ? 'Matching against the store…'
                : busy === 'preview'
                  ? 'Building statements…'
                  : 'Validate statements →'}
            </button>
            {!mappingValid && (
              <Message kind="warning" density="compact">
                Validate waits on the rows marked on pages 2 and 3:{' '}
                {[...new Set(problems.filter((p) => p.column).map((p) => p.text))].slice(0, 3).join(' ')}
              </Message>
            )}
            {stepError}
          </div>
        </div>
      )}

      {/* ---- Step: Validate ---- */}
      {step === 'validate' && preview && profile && (
        <div className="space-y-3">
          {reconcileTruncated && reconcileItems && (
            <p className="text-xs text-tres-warning">
              More values than could be checked against the store — the first{' '}
              {reconcileItems.length} are matched; the rest will be created new.
            </p>
          )}
          {spec && mappings && (
            <RelationshipsWritten
              lines={specRelationships(spec)}
              things={mappings.filter(isThingColumn).length}
              written={preview.report.relations_distinct ?? preview.report.relations_out}
            />
          )}
          <SheetPanel columns={profile.columns} rows={profile.rows} highlight={new Set(focusColumns)} />
          <ValidationStep
            preview={preview}
            rows={profile.rows}
            reconcileItems={reconcileItems ?? []}
            decisions={decisions}
            leftOut={leftOut}
            columnOfPredicate={columnOfPredicate}
            previewOffset={previewOffset}
            busy={busy !== null}
            onDecide={handleDecide}
            onLeaveOut={handleLeaveOut}
            onPutBack={handlePutBack}
            onRecheck={() => void handleRecheck()}
            onPage={(offset) => void doPreview(offset)}
          />

          {/* Target graph — the destination is explicit, never assumed. */}
          <div className="space-y-1">
            <label className="block text-sm font-medium" htmlFor="ss-graph">
              Target graph
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <select
                id="ss-graph"
                value={targetGraph}
                onChange={(e) => setTargetGraph(e.target.value)}
                className={cn(inputCls, 'min-w-72')}
              >
                {!graphs.some((g) => g.bare === targetGraph) && !creatingGraph && (
                  <option value={targetGraph}>{targetGraph}</option>
                )}
                {graphs.map((g) => (
                  <option key={g.bare} value={g.bare} title={g.bare}>
                    {graphDisplayName(g.bare)}
                  </option>
                ))}
                <option value="__new__">＋ New graph…</option>
              </select>
              {creatingGraph && (
                <input
                  type="text"
                  aria-label="Name of the new graph"
                  value={newGraph}
                  placeholder="a name, such as Suppliers"
                  onChange={(e) => setNewGraph(e.target.value)}
                  className={cn(inputCls, 'min-w-72')}
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {effectiveGraph ? (
                <>
                  Data will be written to <strong>{targetName}</strong>{' '}
                  <span className="font-mono">({effectiveGraph})</span>.
                </>
              ) : (
                'Name the new graph.'
              )}
            </p>
          </div>

          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">Write mode</legend>
            <label className="flex items-baseline gap-2 text-sm">
              <input type="radio" name="ss-mode" checked={mode === 'append'} onChange={() => setMode('append')} />
              <span>
                <strong>Append</strong> — add to the data already in this graph.
              </span>
            </label>
            <label className="flex items-baseline gap-2 text-sm">
              <input type="radio" name="ss-mode" checked={mode === 'replace'} onChange={() => setMode('replace')} />
              <span>
                <strong>Replace</strong> — drop{' '}
                <strong>{effectiveGraph ? targetName : 'the target graph'}</strong> first, then
                write. Destroys existing data in that graph.
              </span>
            </label>
          </fieldset>

          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setStep(hasRules ? 'rules' : 'relate')} disabled={busy !== null} className={btnGhost}>
              Back
            </button>
            <button
              type="button"
              onClick={pressImport}
              disabled={busy !== null || uploadsLocked || !effectiveGraph}
              className={btnPrimary}
            >
              {busy === 'commit'
                ? 'Importing…'
                : uploadsLocked
                  ? 'Paused — an import is running'
                  : `Import to ${effectiveGraph ? targetName : '…'}`}
            </button>
            {stepError}
          </div>
        </div>
      )}

      {askSave && profile && spec && (
        <SaveMappingModal
          name={appliedTemplate?.name ?? (templateName.trim() || (file?.name.replace(/\.[^.]+$/, '') ?? ''))}
          applied={appliedTemplate?.name ?? null}
          columns={profile.columns.length}
          relationships={specRelationships(spec).length}
          busy={busy !== null}
          onSaveAndImport={proceedImport}
          onImportOnly={() => proceedImport(null)}
          onCancel={() => setAskSave(false)}
        />
      )}

      {confirmReplace && (
        <ConfirmGraphReplaceModal
          graph={effectiveGraph}
          what="this spreadsheet"
          onCancel={() => setConfirmReplace(false)}
          onConfirm={() => void doCommit(saveAs)}
        />
      )}

      {/* ---- Step: Done ---- */}
      {step === 'done' && result && (
        <div className="space-y-4">
          {result.ok ? (
            <div className={messageClass('outcome')}>
              <div className="font-medium">Imported successfully</div>
              <p className="mt-1 text-sm">
                {(result.data.statements ?? result.data.statements_planned).toLocaleString()}{' '}
                statement
                {(result.data.statements ?? result.data.statements_planned) === 1 ? '' : 's'}{' '}
                written to <span className="font-mono">{result.data.graph}</span>
                {result.data.statements != null &&
                  result.data.statements !== result.data.statements_planned && (
                    <>
                      {' '}
                      ({result.data.statements_planned.toLocaleString()} generated — repeats
                      stored once)
                    </>
                  )}
                :{' '}
                {result.data.entities_minted.toLocaleString()} new{' '}
                {result.data.entities_minted === 1 ? 'entity' : 'entities'} created,{' '}
                {result.data.entities_reconciled.toLocaleString()} matched to existing{' '}
                {result.data.entities_reconciled === 1 ? 'entity' : 'entities'}
                {result.data.relations > 0 && (
                  <>, {result.data.relations.toLocaleString()} relation
                  {result.data.relations === 1 ? '' : 's'}</>
                )}
                {result.data.rows_skipped > 0 && (
                  <>; {result.data.rows_skipped.toLocaleString()} row
                  {result.data.rows_skipped === 1 ? '' : 's'} skipped (reasons in the preview
                  report)</>
                )}
                {result.data.references_dropped > 0 && (
                  <>; {result.data.references_dropped.toLocaleString()} match-only reference
                  {result.data.references_dropped === 1 ? '' : 's'} had no match and were left
                  out</>
                )}
                .
              </p>
              {(() => {
                const sentence = valueUpdatesSentence(result.data.updated, result.data.not_applied_older);
                return sentence ? <p className="mt-1 text-sm">{sentence}</p> : null;
              })()}
              {(result.data.placed?.length ?? 0) > 1 && (
                <p className="mt-1 text-sm">
                  Written where each belongs:{' '}
                  {result.data.placed
                    ?.map((p) => `${p.statements.toLocaleString()} to ${placedName(p.graph)}`)
                    .join(', ')}
                  . New classes and properties are on the Ontology page, and a new list and its
                  terms on the Vocabulary tab.
                </p>
              )}
              {result.data.classes_created != null && result.data.classes_created.length > 0 && (
                <p className="mt-1 text-xs">
                  Created {result.data.classes_created.length} new class
                  {result.data.classes_created.length === 1 ? '' : 'es'} from the class column:{' '}
                  {result.data.classes_created.map((c) => c.label).join(', ')}. Merge any of them
                  into an existing class on the Ontology page.
                </p>
              )}
              {result.data.references_created?.map((r) => (
                <p key={r.column} className="mt-1 text-xs">
                  Created {r.count.toLocaleString()} new{r.class ? ' ' : ''}
                  {r.class && <span className="font-mono">{r.class}</span>} from the {r.column} column
                  because the value matched nothing already in the store: {r.examples.join(', ')}
                  {r.count > r.examples.length ? ', …' : ''}.
                </p>
              ))}
              {result.data.codes_ambiguous?.map((a) => (
                <p key={`${a.column} ${a.code}`} className="mt-1 text-xs text-tres-warning">
                  Left out {a.rows.toLocaleString()} row{a.rows === 1 ? '' : 's'} of the {a.column}{' '}
                  column: code <span className="font-mono">{a.code}</span> names more than one term
                  of <span className="font-mono">{a.class}</span> (
                  <span className="font-mono">{a.terms.join(', ')}</span>). Give each term its own
                  code, then load the rows again.
                </p>
              ))}
              {result.data.entities_minted === 0 && result.data.entities_reconciled > 0 && (
                <p className="mt-1 text-xs">
                  All subjects matched existing entities, so nothing new appears in search or
                  browse counts — the imported values live on those entities. Open one with the
                  graph set to <span className="font-mono">{result.data.graph}</span> to see
                  them.
                </p>
              )}
              <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 font-mono text-xs">
                <dt className="text-muted-foreground">rows</dt>
                <dd>{result.data.rows_in.toLocaleString()}</dd>
                <dt className="text-muted-foreground">payload bytes</dt>
                <dd>{result.data.bytes.toLocaleString()}</dd>
                <dt className="text-muted-foreground">duration_ms</dt>
                <dd>{result.data.duration_ms.toLocaleString()}</dd>
              </dl>
              {result.data.template_id && templateName.trim() && (
                <p className="mt-2 text-xs">
                  Mapping saved as <strong>{templateName.trim()}</strong>; the next file with these
                  columns will use it automatically.
                </p>
              )}
              {result.data.template_error && (
                <p className="mt-2 text-xs text-tres-warning">
                  The data loaded, but the mapping was not saved: {result.data.template_error}
                </p>
              )}
            </div>
          ) : (
            <Message kind="warning" className="whitespace-pre-wrap p-4">
              {result.message}
            </Message>
          )}
          <button type="button" onClick={reset} className={btnSecondary}>
            Import another
          </button>
        </div>
      )}
    </div>
  );

  /** Replace-pair editor for building an ID/key from a cell value (desktop parity).
   *  Each pair is a literal substring replacement applied before the IRI is built. */
  /** Role-specific inline fields for the Map step's Details column. */
}

/** The relationships the import will write, read from the mapping that is
 *  sent, so one that lost them is seen before the commit rather than found in
 *  the store after it. */
function RelationshipsWritten({ lines, things, written }: { lines: string[]; things: number; written: number }) {
  if (lines.length === 0) {
    return things > 1 ? (
      <Message kind="warning" density="compact">
        No ObjectProperty between your {things} Entity columns: their entities will be stored unrelated. Go Back
        to Properties to add them.
      </Message>
    ) : null;
  }
  return (
    <div className="text-sm">
      <p className="font-medium">
        ObjectProperty statements this import writes: {written.toLocaleString()}
      </p>
      <ul className="font-mono text-xs text-muted-foreground">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </div>
  );
}
